//Library Imports
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
//Local Imports
import store, { RootState } from "store";
import EmailList from "../EmailList";
import { getQueryParam } from "utils";
// actions Imports
import {
  addNode,
  clearDropArea,
  addActionModal,
  editNode,
  addWFEmail,
  removeWFEmail,
  setSelectedEmailAction,
  setNodeError,
  shareProfileConfigurationModal,
  saveEmailData,
  showUpdateButton,
  updateWorkflowTriggerEmail,
  resetWorkflowEmails,
  updateErrorPublishNodes,
} from "../../actions";
import { ControlNameEnum, IShareProfileConfigurations } from "../../interfaces";
import { findDropAreaData, findEmailUsage, findUsageOfTriggerEmail } from "../../utils";
import { v4 as uuidv4 } from "uuid";

//Style Imports
import styled from "styled-components";
import { AUTOMATION_EMAILS, AUTOMATION_WORKFLOW } from "constants/types/automationTypes";
import { updateAssetService } from "modules/Services/CampaignsService";
import { getWorkflowDetailsService } from "modules/Services/AutomationServices";

type ISendEmailProps = {
  id: string;
  enableSaveButton: (value: boolean) => void;
};

const SendEmail = (props: ISendEmailProps) => {
  // Props
  const { id, enableSaveButton } = props;

  // Redux States
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const emailData = useSelector((state: RootState) => state.automation.emails.data);
  const showUpdate = useSelector((state: RootState) => state.automation.emails.showUpdate);
  const updateSuccess = useSelector((state: RootState) => state.automation.emails.updateSuccess);
  const choosedEmail = useSelector((state: RootState) => state.automation.emails.selectedEmail);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const actionModalFrom = useSelector((state: RootState) => state.automation.modals.addAction.from);
  const workflowDetail = useSelector((state: RootState) => state.automation.workflow.detail);
  const triggerId: number = useSelector((state: RootState) => state.automation.workflow.detail.triggerId);
  const triggerEmailId: number = useSelector((state: RootState) => state.automation.workflow.detail.emailId);
  const shareProfileConfigurations: IShareProfileConfigurations = useSelector(
    (state: RootState) => state.automation.workflow.shareProfileConfigurations
  );
  const shareProfileUuid: string = useSelector(
    (state: RootState) => state.automation.workflow.detail.shareProfileUuid
  );
  const workflowId = useSelector((state: RootState) => state.automation.workflow.detail.id);
  const emailForm = useSelector((state: RootState) => state.automation.emails.form);
  const { first_name, last_name } = useSelector((state: RootState) => state.user.userData);

  // Local Variables
  const fullName = first_name + " " + last_name;

  // Local States
  const [selectedEmail, setSelectedEmail] = useState(choosedEmail);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [loadingEmailUpdate, setLoadingEmailUpdate] = useState(false);

  // Local Variables
  const dispatch = useDispatch();

  // Component Logic
  useEffect(() => {
    if (selectedEmail?.id === 0) {
      enableSaveButton(false);
    } else {
      enableSaveButton(true);
    }
    return () => {
      enableSaveButton(false);
    };
  }, []);

  // useEffect to set the data of edit Node
  const isEmailModalOpen = modal.visible === true && modal.tab === ControlNameEnum.SEND_EMAIL;
  let dependencies = itemData && isEmailModalOpen;
  useEffect(() => {
    if (isEmailModalOpen && itemData) {
      if (itemData.hasOwnProperty("emailId")) {
        const emailId = itemData["sp_uuid"] ? 0.5 : itemData.emailId;
        setSelectedEmail({
          id: emailId, // if email is share profile then put 0.5 in the id
          title: itemData.emailName,
          campaignId: 0,
        });
        setSelectedEmailAction(emailId, itemData.emailName, 0);
        enableSaveButton(true);
      }
    } else if (actionModalFrom === "trigger") {
      setSelectedEmail({
        id: triggerEmailId,
        title: workflowDetail.emailName,
        campaignId: workflowDetail.campaignId,
      });
      setSelectedEmailAction(triggerEmailId, workflowDetail.emailName, workflowDetail.campaignId);
    }
  }, [dependencies, actionModalFrom]);

  const closeModal = () => {
    dispatch(addActionModal(false));
    dispatch(clearDropArea());
  };

  const isFormValid = () => {
    let isError = !emailForm.sendFrom || (!emailForm.senderName && !fullName) || !emailForm.emailSubject;

    let array = [];
    if (!emailForm.senderName && !fullName) array.push("Sender Name");
    if (!emailForm.sendFrom) array.push("Send From");
    if (!emailForm.emailSubject) array.push("Email Subject");
    setErrorFields && setErrorFields(array);

    return isError;
  };

  const isFormValidFromData = (emailForm?: any) => {
    let isError = !emailForm.sendFrom || (!emailForm.senderName) || !emailForm.emailSubject;
    let array = [];
    if (!emailForm.senderName) array.push("Sender Name");
    if (!emailForm.sendFrom) array.push("Send From");
    if (!emailForm.emailSubject) array.push("Email Subject");
    setErrorFields && setErrorFields(array);
    return isError;
  };

  const submitHandler = (event: any) => {
    if (!showUpdate) {
      // onClick Save Button
      event.preventDefault();
      if (actionModalFrom === "trigger") {
        // If editing Trigger node email
        const workflowId = getQueryParam("workflow_id");
        updateWorkflowTriggerEmail(parseInt(workflowId), selectedEmail?.id, selectedEmail?.title, selectedEmail?.campaignId);
        // For Error detection
        const hasEmailChanged = selectedEmail.id !== triggerEmailId;
        if (hasEmailChanged) {
          let ids = findUsageOfTriggerEmail(triggerEmailId);
          ids.forEach((item: string) => {
            dispatch(setNodeError(item, true));
          });
        }
      } else {
        // Any node other than trigger node Case
        let obj = findDropAreaData(ControlNameEnum.SEND_EMAIL);
        let title = `: ${selectedEmail?.title}`;
        const email = emailData.find((email) => email.emailId === selectedEmail.id);
        if (id) {
          // edit Node
          const newUuid = uuidv4();
          // configurations equal to undefined means user want to change the simple email to share profile email
          // thats why we have to add sp_uuid in the object
          const hasConfigurationUndefined = itemData?.configurations === undefined;
          // delete the configurations and sp_uuid so that if user wants to change from share profile email to simple email.
          delete itemData.configurations;
          if (selectedEmail.id !== 0.5) {
            delete itemData.sp_uuid;
          }
          dispatch(
            editNode(id, {
              ...itemData,
              emailId:
                selectedEmail.id === 0.5 ? shareProfileConfigurations.selectedTemplate.value : selectedEmail.id,
              emailName: selectedEmail.id === 0.5 ? shareProfileConfigurations?.emailSubject : selectedEmail.title,
              emailType: selectedEmail.id === 0.5 ? "share profile" : "email",
              versionEmailType: email?.versionEmailType,
              designReady: email?.designReady,
              nodeTitle: selectedEmail.id === 0.5 ? `: ${shareProfileConfigurations?.emailSubject}` : title,
              ...(hasConfigurationUndefined && selectedEmail.id === 0.5 ? { sp_uuid: newUuid } : {}),
              ...(selectedEmail.id === 0.5 ? { configurations: shareProfileConfigurations } : {}),
            })
          );
          // if email does not change then don't show the error
          const hasEmailChanged =
            selectedEmail.id === 0.5
              ? shareProfileConfigurations.selectedTemplate.value !==
              itemData?.configurations?.selectedTemplate.value
              : selectedEmail.id !== itemData?.emailId;
          if (hasEmailChanged) {
            let ids = findEmailUsage(id, itemData?.emailId);
            ids.forEach((item: string) => {
              dispatch(setNodeError(item, true));
            });
          }
          dispatch(updateErrorPublishNodes(id))
          dispatch(removeWFEmail(itemData?.emailId));
        } else {
          // add node
          const newUuid = uuidv4();
          dispatch(
            addNode({
              ...obj,
              emailId:
                selectedEmail.id === 0.5 ? shareProfileConfigurations.selectedTemplate.value : selectedEmail.id,
              emailName: selectedEmail.id === 0.5 ? shareProfileConfigurations?.emailSubject : selectedEmail.title,
              nodeTitle: selectedEmail.id === 0.5 ? `: ${shareProfileConfigurations?.emailSubject}` : title,
              emailType: selectedEmail.id === 0.5 ? "share profile" : "email", // 10 is for shared email
              versionEmailType: email?.versionEmailType,
              designReady: email?.designReady,
              campaignId: selectedEmail.campaignId,
              ...(selectedEmail.id === 0.5 ? { sp_uuid: newUuid } : {}),
              ...(selectedEmail.id === 0.5 ? { configurations: shareProfileConfigurations } : {}),
            })
          );
        }
        dispatch(addWFEmail(selectedEmail?.id));
      }
      setSelectedEmail({
        id: 0,
        title: "",
        campaignId: 0,
      });
      setSelectedEmailAction(0, "", 0);
      setErrorFields([]);
      closeModal();
    } else {
      // OnClick Update Button
      event.preventDefault();
      if (!isFormValid()) {
        if (emailForm.emailSubject !== emailForm.currentData.emailSubject) {
          // if old subject name is different from new subject name
          updateAssetService(emailForm.emailSubject, selectedEmail.id.toString());
          getWorkflowDetailsService(workflowId)
            .then((res: any) => {
              if (res?.data?.success) {
                dispatch({
                  type: AUTOMATION_WORKFLOW.LOAD_SUCCESS,
                  payload: res.data.data,
                });
              }
              if (id)
                dispatch(
                  editNode(id, {
                    emailName: emailForm.emailSubject,
                    nodeTitle: `: ${emailForm.emailSubject}`,
                  })
                );
              dispatch({
                type: AUTOMATION_WORKFLOW.UPDATE_SAME_EMAIL_NODES_TITLE,
                payload: { emailSubject: emailForm.emailSubject, emailId: emailForm.emailId },
              });
            })
            .catch((err: any) => { });
          setSelectedEmail({
            id: emailForm.emailId,
            title: emailForm.emailSubject,
            campaignId: emailForm.campaignId,
          });
          setSelectedEmailAction(emailForm.emailId, emailForm.emailSubject, emailForm.campaignId);
        }
        setLoadingEmailUpdate(true);
        let values = {
          sendFrom: emailForm.sendFrom,
          senderName: emailForm.senderName,
          subject: emailForm.emailSubject,
          campaignId: emailForm.campaignId,
          emailId: emailForm.emailId,
          versionEmailType: emailForm.versionEmailType,
          designReady: emailForm.designReady,
        };

        saveEmailData(values)
          .then((res) => {
            if (res?.data?.success) {
              dispatch({
                type: AUTOMATION_EMAILS.UPDATE_EMAIL_DATA,
                payload: values,
              });
              setLoadingEmailUpdate(false);
            }
          })
          .catch(() => {
            // store.dispatch({ type: SAVE_PUBLISH_STATE.FAIL });
          });
        setErrorFields && setErrorFields([]);
        showUpdateButton(false);
        // Update Successful message check value update
        store.dispatch({
          type: AUTOMATION_EMAILS.SET_UPDATE_SUCCESS,
          payload: true,
        });
        // Updating Current Form Data
        store.dispatch({
          type: AUTOMATION_EMAILS.UPDATE_FORM_CURRENT_DATA,
          payload: {
            emailSubject: emailForm.emailSubject,
            senderName: emailForm.senderName,
            sendFrom: emailForm.sendFrom,
          },
        });
      }
    }
  };

  const changeSelectedEmail = (id: number) => {
    const email = emailData.find((email) => email.emailId === id);
    let emailTitle = "";
    let error = false;
    if (email) {
      error = !emailForm.sendFrom || !emailForm.emailSubject || (!emailForm.senderName ?? !fullName);
      let array = [];
      if (!emailForm.senderName && !fullName) array.push("Sender Name");
      if (!emailForm.sendFrom) array.push("Send From");
      if (!emailForm.emailSubject) array.push("Email Subject");
      setErrorFields(array);
      emailTitle = email.title;
      setSelectedEmail({
        id: email.emailId,
        title: emailTitle,
        campaignId: email.campaignId,
      });
    } else if (id === 0.5) {
      emailTitle = "Share Profile";
      setSelectedEmail({
        id: id,
        title: emailTitle,
        campaignId: 0,
      });
      enableSaveButton(false);
    }
    setSelectedEmailAction(id, emailTitle, email?.campaignId);
  };

  const handleSetErrors = (array: string[]) => {
    setErrorFields(array);
    if (array.length > 0) enableSaveButton(false);
  };

  useEffect(() => {
    if (selectedEmail.id) {
      changeSelectedEmail(selectedEmail.id);
    }
  }, [emailData.length]);

  useEffect(() => {
    if (selectedEmail?.id) {
      if (emailForm.emailId === selectedEmail.id) {
        if (!isFormValidFromData(emailForm)) {
          enableSaveButton(true);
        }
      }
    }
  }, [selectedEmail.id, emailForm]);

  useEffect(() => {
    if (!actionModalVisible) {
      // setSelectedEmail({
      //   id: 0,
      //   title: "",
      //   campaignId: 0,
      // });
      // setSelectedEmailAction(0, "", 0);
      setErrorFields([]);
      // Resetting the form data to currentData
      store.dispatch({
        type: AUTOMATION_EMAILS.RESET_FORM_DATA,
        payload: {
          emailSubject: emailForm.currentData.emailSubject,
          senderName: emailForm.currentData.senderName,
          sendFrom: emailForm.currentData.sendFrom,
        },
      });
      showUpdateButton(false);
    }
  }, [actionModalVisible]);

  // Resetting Update Success variable after 5 secs
  useEffect(() => {
    if (updateSuccess === true) {
      const timer = setTimeout(() => {
        store.dispatch({
          type: AUTOMATION_EMAILS.SET_UPDATE_SUCCESS,
          payload: false,
        });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [updateSuccess]);

  return (
    <Wrapper>
      <div className="d-flex align-items-start">
        <EmailList
          selectedEmailId={selectedEmail?.id}
          changeHandler={changeSelectedEmail}
          isActionModal
          isEditMode={!!id}
          loadingEmailUpdate={loadingEmailUpdate}
          submitHandler={submitHandler}
          isWorkflow
          errorFields={errorFields}
          setErrorFields={handleSetErrors}
          isFormValid={isFormValid}
          enableSaveButton={enableSaveButton}
        />
      </div>
    </Wrapper>
  );
};

export default SendEmail;

const Wrapper = styled.div`
  .overflow-scroll {
    max-height: 570px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: 490px;
    }
  }
  .done-button {
    margin-top: -60px;
    padding-bottom: 20px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: -47px;
    }
  }
  .trigger-sidebar {
    border-right: 1px solid #dddddd;
    width: 265px;
    height: calc(100vh - 290px);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 550px;
    }
    h4 {
      color: #424b50;
      font-size: 20px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      margin: 22px 24px 10px 24px;
    }
    .tabs-item {
      padding: 12px 0 8px 27px;
      background-color: #e6f5ea;
      width: 100%;
      color: #424b50;
      font-size: 16px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      position: relative;
      &:before {
        content: "";
        width: 12px;
        height: 100%;
        background-color: #1ec252;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }
`;
